import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Lock, CheckCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ResetPassword = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get('token') || '';

  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error(language === 'fr' ? 'Lien invalide' : 'Invalid link');
    }
  }, [token, language]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error(language === 'fr' ? 'Au moins 8 caractères' : 'At least 8 characters');
      return;
    }
    if (pw !== confirm) {
      toast.error(language === 'fr' ? 'La confirmation ne correspond pas' : 'Confirmation mismatch');
      return;
    }
    setBusy(true);
    try {
      await authApi.resetPassword(token, pw);
      setDone(true);
      toast.success(language === 'fr' ? 'Mot de passe réinitialisé' : 'Password reset');
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'fr' ? 'Échec de la réinitialisation' : 'Reset failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-4">
      <Card className="w-full max-w-md border border-[#E2E8F0] shadow-sm" data-testid="reset-password-page">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <Lock size={22} className="text-[#0052FF]" weight="duotone" />
            {language === 'fr' ? 'Réinitialiser le mot de passe' : 'Reset password'}
          </CardTitle>
          <CardDescription>
            {language === 'fr'
              ? 'Choisissez un nouveau mot de passe (8 caractères minimum).'
              : 'Choose a new password (8 chars min).'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!token ? (
            <div className="flex items-center gap-2 text-red-600 font-manrope">
              <Warning size={18} />
              {language === 'fr' ? 'Lien manquant ou invalide.' : 'Missing or invalid link.'}
            </div>
          ) : done ? (
            <div className="flex items-center gap-2 text-emerald-700 font-manrope">
              <CheckCircle size={22} weight="duotone" />
              {language === 'fr' ? 'Redirection vers la page de connexion…' : 'Redirecting to login…'}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="font-manrope">{language === 'fr' ? 'Nouveau mot de passe' : 'New password'}</Label>
                <Input
                  type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                  data-testid="reset-new-password" autoFocus required minLength={8}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-manrope">{language === 'fr' ? 'Confirmer' : 'Confirm'}</Label>
                <Input
                  type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-password" required minLength={8}
                />
              </div>
              <Button
                type="submit" disabled={busy}
                className="w-full bg-[#0052FF] hover:bg-[#0040CC]"
                data-testid="reset-submit"
              >
                {busy
                  ? (language === 'fr' ? 'Enregistrement…' : 'Saving…')
                  : (language === 'fr' ? 'Réinitialiser' : 'Reset')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
